import { configureStore as configureStoreRTK } from '@reduxjs/toolkit';
import { helpSlice } from 'client/modules/Help/helpSlice';
import { peopleApiSlice } from 'client/modules/People/peopleApiSlice';
import { apiKeysSlice } from 'client/modules/Surveys/apiKeysSlice';
import { mcpOAuthSlice } from 'client/modules/Settings/mcpOAuthSlice';
import { folderApiSlice } from 'client/modules/Surveys/folderApiSlice';
import { linkApiSlice } from 'client/modules/Surveys/linkApiSlice';
import { savedSearchApiSlice } from 'client/modules/Surveys/savedSearchApiSlice';
import { surveyAnalyzeApiSlice } from 'client/modules/Surveys/surveyAnalyzeApiSlice';
import { surveyDeliveryApiSlice } from 'client/modules/Surveys/surveyDeliveryApiSlice';
import { surveyDriverApiSlice } from 'client/modules/Surveys/surveyDriverApiSlice';
import { sessionApiSlice } from 'client/modules/Auth/sessionApiSlice';
import throttle from 'lodash/throttle';
/**
 * Main store function
 */
import createSocketIoMiddleware from 'redux-socket.io';
import { io } from 'socket.io-client';
import Cookies from 'universal-cookie';
import { enabledLanguages } from '../Intl/setup';
import { createI18nData, getI18n } from './AppReducer';
import {} from './modules/Auth/AuthReducer';
import { loadState, saveState } from './persistedState';

import appReducer from './reducers';
import mergeDeep from './util/mergeDeep';
import '../server/config';

const configureStore = (initialState = {}, req, res) => {
  let combinedInitialState = initialState;
  const generatedState = { app: { i18n: {} }, auth: {} };
  const enhancers = [];
  if (process.env.CLIENT) {
    // Let's not send data to Google or anywhere outside EU. :)
    // const analyticsMiddleware = createMiddleware(eventsMap, GoogleTagManager());

    const socket = io({
      transports: ['websocket', 'polling'], // Prefer WebSocket, fallback to polling
      forceNew: true, // Don't reuse connections
      rememberUpgrade: false, // Don't remember transport upgrades
      autoConnect: true, // Connect automatically
    });
    const socketIoMiddleware = createSocketIoMiddleware(socket, 'SOCKET_');

    // Middleware and store enhancers
    // enhancers.push(applyMiddleware(analyticsMiddleware)); // create and push ReduxGTM
    enhancers.push(socketIoMiddleware);

    if (process.env.NODE_ENV === 'development') {
      const Debug = require('redux-debug');
      const debug = require('debug')('redux');
      enhancers.push(Debug(debug)); // change debug by console.log()?
    }

    const persistedState = loadState() || {};
    combinedInitialState = mergeDeep(persistedState, initialState);

    if (combinedInitialState.app && combinedInitialState.app.i18n) {
      combinedInitialState.app.i18n = createI18nData(
        combinedInitialState,
        combinedInitialState.app.i18n.locale,
      );
    }

    const urlLocale = window.location.pathname.match(/^\/(en|es)(\/|$)/)?.[1];
    if (urlLocale) {
      combinedInitialState.app = combinedInitialState.app || {};
      combinedInitialState.app.i18n = createI18nData(combinedInitialState, urlLocale);
    }

    generatedState.app.userAgent = navigator.userAgent;
  } else {
    generatedState.app.userAgent = req && req.headers ? req.headers['user-agent'] : 'all';
    const urlLocale =
      req.baseUrl === '/en' || req.baseUrl === '/es' ? req.baseUrl.slice(1) : null;
    const cookies = new Cookies(req.headers?.cookie);
    let locale = cookies.get('locale');

    if (urlLocale) {
      locale = urlLocale;
    } else if (req.profile && req.profile.locale && enabledLanguages.indexOf(req.profile.locale) >= 0) {
      locale = req.profile.locale;
    } else if (enabledLanguages.indexOf(locale) < 0) {
      ({ locale } = req); // Accept-Language via locale middleware
    }
    if (enabledLanguages.indexOf(locale) < 0) {
      [locale] = enabledLanguages; // Should never happen
    }
    generatedState.app.i18n = createI18nData(generatedState, locale);

    // Restore auth state from req.profile if available
    if (req.profile) {
      generatedState.auth = {
        ...generatedState.auth,
        profile: {
          ...req.profile,
        },
      };
    }
  }

  combinedInitialState = mergeDeep(generatedState, combinedInitialState);

  const rootReducer = (state, action) => {
    let newState = state;
    if (action.type === 'RECEIVE_LOGOUT') {
      newState = initialState;
      newState.app = state.app;
    }
    return appReducer(newState, action);
  };
  const store = configureStoreRTK({
    reducer: rootReducer,
    preloadedState: combinedInitialState,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        thunk: { extraArgument: { req, res } },
        immutableCheck: {
          ignoredPaths: [
            'app',
            'app2',
            'auth',
            'confirm',
            'content',
            'emailPreferences',
            'integrations',
            'pairwise',
            'people',
            'pricing',
            'responses',
            'routing',
            'settings',
            'subscriptions',
            'surveys',
            'twoFactor',
            'passkeys',
          ],
        },
        serializableCheck: {
          ignoredActions: ['confirm/open', 'SET_ERROR', 'SET_OK', 'SET_INFO'],
          ignoredPaths: [
            'app',
            'app2',
            'auth',
            'confirm',
            'content',
            'emailPreferences',
            'integrations',
            'pairwise',
            'people',
            'pricing',
            'responses',
            'routing',
            'settings',
            'subscriptions',
            'surveys',
            'twoFactor',
            'passkeys',
          ],
        },
      }).concat([
        apiKeysSlice.middleware,
        mcpOAuthSlice.middleware,
        folderApiSlice.middleware,
        helpSlice.middleware,
        linkApiSlice.middleware,
        peopleApiSlice.middleware,
        savedSearchApiSlice.middleware,
        surveyAnalyzeApiSlice.middleware,
        surveyDeliveryApiSlice.middleware,
        surveyDriverApiSlice.middleware,
        sessionApiSlice.middleware,
        ...enhancers,
      ]),
  });

  // Only keep essential persistence for app state (not auth cookies)
  if (process.env.CLIENT) {
    let previousLocale = null;

    store.subscribe(
      throttle(() => {
        const state = store.getState();
        const i18n = getI18n(state);
        const stateToSave = {
          app: {},
        };
        if (i18n) {
          stateToSave.app.i18n = { locale: i18n.locale };
        }
        saveState(stateToSave);

        // Set minimal locale cookie for SSR (only when locale actually changes)
        if (i18n && i18n.locale && previousLocale !== i18n.locale) {
          if (previousLocale) {
            const cookies = new Cookies();
            const secure = process.env.NODE_ENV === 'production';
            cookies.set('locale', i18n.locale, {
              maxAge: 365 * 24 * 60 * 60, // 1 year - locale is not sensitive
              secure,
              httpOnly: false,
              path: '/',
              sameSite: 'lax',
            });
          }
          previousLocale = i18n.locale;
        }
      }, 1000),
    );
  }

  // For hot reloading reducers
  if (module.hot) {
    // Enable Webpack hot module replacement for reducers
    module.hot.accept('./reducers', () => {
      const nextReducer = require('./reducers').default;
      store.replaceReducer(nextReducer);
    });
  }

  return store;
};

export default configureStore;
